<template>
  <div
    class="rounded-2xl overflow-hidden cursor-pointer transition-all duration-200 backdrop-blur-xl border hover:-translate-y-1"
    :class="
      isLight
        ? 'bg-primary-1000 border-[#1a9b8a]/25 hover:border-[#1a9b8a]/50 shadow-sm hover:shadow-lg hover:shadow-[#1a9b8a]/10'
        : 'bg-primary-1000 border-white/[0.08] hover:border-[#1a9b8a]/35 shadow-md shadow-black/30 hover:shadow-xl hover:shadow-black/40'
    "
    @click="viewEventDetails"
  >
    <!-- POSTER AREA -->
    <div class="relative w-full overflow-hidden" style="aspect-ratio: 3/4">
      <!-- Poster art / image -->
      <div
        class="absolute inset-0 flex items-center justify-center overflow-hidden"
        :class="isLight ? 'bg-primary-800' : 'bg-primary-1000'"
      >
        <img
          v-if="event.ImageURL"
          :src="event.ImageURL"
          :alt="event.Title"
          class="absolute inset-0 w-full h-full object-cover"
        />
        <span
          v-else
          class="absolute font-display font-black text-[60px] leading-[0.9] tracking-[-5px] text-center pointer-events-none select-none"
          :class="isLight ? 'text-black/10' : 'text-white/[0.05]'"
        >
          {{ event.posterGhost }}
        </span>
      </div>

      <!-- Top scrim -->
      <div
        class="absolute top-0 left-0 right-0 h-2/5 bg-gradient-to-b from-[#0f1f1c]/40 to-transparent"
      ></div>

      <!-- Bottom scrim -->
      <div
      class="absolute bottom-0 left-0 right-0 h-3/4 
        bg-gradient-to-t 
        from-black/95 
        via-black/70 
        to-transparent
        backdrop-blur-[1px]"
      ></div>

      <!-- Top row: status pill + save -->
      <div
        class="absolute top-2.5 left-2.5 right-2.5 flex items-center justify-between z-10"
      >
        <!-- Status pill -->
        <div
          v-if="event.status === 'live'"
          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500 text-white text-[8px] font-black tracking-wider uppercase"
        >
          <span
            class="w-1 h-1 rounded-full bg-white animate-pulseFast flex-shrink-0"
          ></span>
          {{ $t('status.live') }}
        </div>
        <div
          v-else-if="event.status === 'soon'"
          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-400 border border-amber-400/30 text-[8px] font-bold tracking-wider uppercase"
        >
          <span
            class="w-1 h-1 rounded-full bg-amber-400 animate-pulseFast flex-shrink-0"
          ></span>
          {{ $t('status.soon') }}
        </div>
        <div
          v-else-if="event.status === 'rsvp'"
          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[8px] font-bold tracking-wider uppercase border transition-all duration-200 cursor-pointer bg-[#1a9b8a]/15 text-[#a8d5cc] border-[#1a9b8a]/30 hover:bg-[#1a9b8a] hover:text-white hover:border-[#1a9b8a]"
        >
          {{ $t('status.rsvp') }}
        </div>
        <div
          v-else-if="event.status === 'ended'"
          class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-bold tracking-widest uppercase border border-white/10 text-white/40 bg-black/30 backdrop-blur-md"
        >
          {{ $t('status.ended') }}
        </div>

        <!-- Save button -->
        <button
          class="w-6 h-6 rounded-full flex items-center justify-center border transition-all duration-200 backdrop-blur-md"
          :class="[
            isSaved
              ? 'bg-[#1a9b8a]/20 text-[#a8d5cc] border-[#1a9b8a]/40'
              : 'bg-black/50 text-white/80 border-white/15 hover:bg-[#1a9b8a]/20 hover:text-[#a8d5cc] hover:border-[#1a9b8a]/40',
          ]"
          @click.stop="isSaved = !isSaved"
          aria-label="Save event"
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 24 24"
            :fill="isSaved ? 'currentColor' : 'none'"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      </div>

      <!-- Content overlay -->
      <div class="absolute text-left bottom-0 left-0 right-0 z-10 px-2.5 pb-0">
        <p class="text-[9px] font-medium tracking-wide mb-1 text-white/80">
          {{ formatEventDate(event.date) }} · {{ event.location_name }}
        </p>
        <h3
          class="font-display font-black text-[13px] leading-[1.1] tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)] mb-1"
        >
          {{ event.Title }}
        </h3>
        <p v-for="org in event.organizer" :key="org" class="font-display font-black text-[10px] leading-[1.4] mb-1.5 text-white/80">
          {{ org.Name }}
        </p>

        <!-- Tags -->
        <div class="flex flex-wrap gap-1 mb-2">
          <span
            v-for="tag in event.tags"
            :key="tag"
            class="text-[9px] font-medium transition-colors duration-200 text-white/60 hover:text-[#a8d5cc]"
          >
            {{ tag }}
          </span>
        </div>

        <!-- Footer: price + avatars -->
        <div class="flex items-start justify-between pb-2.5">
          <div>
            <p
              class="font-display font-black text-sm leading-none text-[#a8d5cc]"
            >
              {{ event.price }}
            </p>
            <p class="text-[8px] mt-0.5 text-white/30">{{ event.priceSub }}</p>
          </div>
          <div class="flex items-start gap-1">
            <div class="flex">
              <div
                v-for="av in event.attendees"
                :key="av.initials"
                class="w-4 h-4 rounded-full border-2 border-[#0f1f1c] -ml-1.5 first:ml-0 flex items-center justify-center text-[6px] font-bold text-white"
                :class="av.bg"
              >
                {{ av.initials }}
              </div>
            </div>
            <span class="text-[9px] text-white">
              {{ $t('eventsGrid.attendees') }} {{event.capacity}}+
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "EventCardComponent",
  props: {
    event: { type: Object, required: true },
    isLight: Boolean,
  },
  data() {
    return { isSaved: this.event.saved || false };
  },
  methods: {
    viewEventDetails() {
      if (this.event.id) {
        this.$router.push({
          name: "event-details",
          params: { id: this.event.id },
        });
      } else {
        console.error("Event ID is missing");
      }
    },
    
    formatEventDate(date) {
      return new Date(date).toLocaleDateString(
        this.$i18n.locale === 'it' ? 'it-IT' : 'en-GB',
        {
          day: "numeric",
          month: "short",
          year: "numeric",
        }
      );
    }
  },
};
</script>